/**
 * Vincent #262/#269 (2026-06-11) — Fenêtre apprenant d'un parcours.
 *
 * Ouverte au clic sur la tuile d'un parcours (vue apprenant) OU via le bouton
 * "Visualiser" (vue éditeur → Mode de démonstration).
 *
 * Contenu :
 *   - liste ordonnée des vidéos, chacune cliquable pour la consulter
 *   - bouton Quiz par vidéo (Q&R approuvées de CETTE vidéo)
 *   - état "complétée" par vidéo (≥30s visionnées + quiz 3+ questions)
 *   - CTA quiz du parcours
 *   - bandeau "Mode de démonstration" si demoMode=true
 */
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle, Path, PlayCircle, X, Eye, FileText } from "@phosphor-icons/react";
import { usePathwayQuizEligibility, pathwayQuizButtonClasses } from "../../lib/usePathwayQuizEligibility";

function L(lang, fr, en) { return lang === "fr" ? fr : en; }

/**
 * Vincent XLSX 2026-07-13 21h Row 451 — Bouton coloré selon éligibilité.
 * Placed OUTSIDE the main component to keep the modal signature small.
 */
function PathwayQuizButtonWithEligibility({ pathwayId, lang, onNavigate, bestQuizScore }) {
  const { loading, eligibility } = usePathwayQuizEligibility(pathwayId);
  if (loading || !eligibility) {
    return <span className="text-[11px] text-zinc-400 italic">…</span>;
  }
  const isLocked = eligibility.state === "locked";
  const label = eligibility.state === "passed"
    ? `✓ ${L(lang, "Quiz du parcours réussi", "Pathway quiz passed")}`
    : `🎯 ${L(lang, "Faire le quiz du parcours", "Take the pathway quiz")}`;
  const scoreSuffix = (eligibility.pathway_quiz_best_score ?? bestQuizScore) != null
    ? ` (${Math.round(eligibility.pathway_quiz_best_score ?? bestQuizScore)}%)`
    : "";
  if (isLocked) {
    return (
      <button
        type="button"
        onClick={() => toast.info(eligibility.reason_fr, { duration: 6000 })}
        className={pathwayQuizButtonClasses("locked")}
        data-testid="pathway-learner-quiz-btn"
        title={eligibility.reason_fr}
      >
        🔒 {L(lang, "Quiz du parcours verrouillé", "Pathway quiz locked")}
      </button>
    );
  }
  return (
    <Link
      to={`/dashboard/quiz?pathway_id=${encodeURIComponent(pathwayId)}&num_questions=7&auto=1`}
      onClick={onNavigate}
      className={pathwayQuizButtonClasses(eligibility.state)}
      data-testid="pathway-learner-quiz-btn"
      title={eligibility.reason_fr}
    >
      {label}{scoreSuffix}
    </Link>
  );
}

export default function PathwayLearnerModal({ pathway, videos = [], progress = null, bestQuizScore = null, demoMode = false, lang = "fr", onClose }) {
  const completedIds = new Set(progress?.completed_video_ids || []);
  // Vincent XLS iter103 (items 20, 21) — progression détaillée par vidéo
  const vidProg = progress?.videos_progress || {};
  // Vincent #293 (2026-06-12) — Tri alphabétique A→Z par défaut dans la vue
  // apprenant (aucun filtre n'est exposé ici), conformément à la consigne :
  // l'ordre d'affichage doit être prévisible pour l'apprenant.
  const sortedVideos = [...videos].sort((a, b) =>
    (a.title || "").localeCompare(b.title || "", "fr", { sensitivity: "base" })
  );
  const themeCounts = {};
  sortedVideos.forEach((v) => {
    if (v.training_theme) themeCounts[v.training_theme] = (themeCounts[v.training_theme] || 0) + 1;
  });
  const dominantTheme = Object.entries(themeCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || "all";
  const pct = progress?.percent ?? null;

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="pathway-learner-modal-overlay"
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid={`pathway-learner-modal-${pathway.id}`}
      >
        {/* Bandeau démo (Vincent #269 — pour les éditeurs qui "Visualisent") */}
        {demoMode && (
          <div className="px-5 py-2 bg-amber-100 border-b border-amber-200 text-amber-800 text-xs font-bold flex items-center gap-2" data-testid="pathway-demo-banner">
            <Eye size={14} weight="bold" />
            {L(lang, "Mode de démonstration — vue telle que présentée aux apprenants", "Demo mode — view as presented to learners")}
          </div>
        )}

        {/* Header */}
        <div className="px-5 py-4 border-b border-zinc-200 bg-gradient-to-r from-[#002FA7]/5 to-transparent flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Path size={20} weight="duotone" className="text-[#002FA7] flex-shrink-0" />
              <h2 className="font-bold text-lg text-zinc-900 truncate">{pathway.name}</h2>
            </div>
            {pathway.description && <p className="text-sm text-zinc-600">{pathway.description}</p>}
            <div className="mt-2 flex items-center gap-3 text-[11px] text-zinc-500">
              <span>{sortedVideos.length} {L(lang, "vidéo(s) à visionner", "video(s) to watch")}</span>
              {pct !== null && (
                <span>· {L(lang, "Progression", "Progress")}: <strong className="text-[#002FA7]">{pct}%</strong></span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-zinc-100 rounded flex-shrink-0"
            data-testid="pathway-learner-modal-close"
            title={L(lang, "Fermer", "Close")}
          >
            <X size={18} />
          </button>
        </div>

        {/* Liste des vidéos */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-3">
            {L(lang, "Formations à visionner dans l'ordre", "Trainings to watch in order")}
          </h3>
          {sortedVideos.length === 0 ? (
            <p className="text-sm text-zinc-400 italic py-4 text-center">
              {L(lang, "Aucune vidéo dans ce parcours.", "No videos in this pathway.")}
            </p>
          ) : (
            <ol className="space-y-2" data-testid="pathway-learner-video-list">
              {sortedVideos.map((v, idx) => {
                const isDone = completedIds.has(v.id);
                // Vincent XLS iter103 (items 20, 21) — Indicateurs détaillés par vidéo
                const vp = vidProg[v.id] || {};
                const watched = vp.watched;          // ≥30 s visionnées
                const watchPct = vp.watch_pct || 0;  // % de durée vu
                const watchSec = vp.watch_seconds || 0;
                const quizScore = vp.quiz_score;     // null si jamais fait
                const quizPassed = vp.quiz_passed;   // score ≥60
                // Vincent XLSX 2026-07-07 22h Row #5 (Eric 2026-07-08 SPEC STRICK)
                // — Format [hh:mm:ss] au lieu de "1 min 30 s" comme demandé
                // explicitement par Vincent : « la durée visualisée (en [hh:mm:ss]) ».
                const fmtDur = (s) => {
                  const total = Math.max(0, Math.floor(Number(s) || 0));
                  const h = Math.floor(total / 3600);
                  const m = Math.floor((total % 3600) / 60);
                  const sec = total % 60;
                  const pad = (n) => String(n).padStart(2, "0");
                  return `${pad(h)}:${pad(m)}:${pad(sec)}`;
                };
                return (
                  <li key={v.id} className="flex items-center gap-2">
                    <Link
                      to={`/dashboard/learner/videos/${v.id}?pathway_id=${pathway.id}${demoMode ? "&demo=1" : ""}`}
                      onClick={onClose}
                      className="flex-1 flex items-center gap-3 p-3 rounded-lg border border-zinc-200 hover:border-[#002FA7] hover:bg-[#002FA7]/5 transition-all group"
                      data-testid={`pathway-learner-video-${v.id}`}
                    >
                      <span className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold flex-shrink-0 transition-colors ${
                        isDone ? "bg-teal-100 text-teal-700" : "bg-zinc-100 group-hover:bg-[#002FA7] text-zinc-600 group-hover:text-white"
                      }`}>
                        {isDone ? <CheckCircle size={16} weight="fill" /> : idx + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${watched ? "text-teal-600 font-semibold" : "text-zinc-900"}`} title={v.title}>
                          {/* Vincent #374 (Eric 2026-06-23 SPEC STRICK) — fallback intelligent au lieu de blanc */}
                          {v.title || (v.original_filename || "").replace(/\.[^.]+$/, "").replace(/_/g, " ").trim() || L(lang, "(Sans titre)", "(Untitled)")}
                        </p>
                        {/* Vincent #317 (Eric 2026-06-22 SPEC STRICK 3e passe) — Ligne SOUS LE TITRE
                            avec check vert + durée + (%) + "Terminé" si 100%. */}
                        <p className="text-[11px] text-zinc-500 flex items-center gap-1.5 flex-wrap">
                          {v.training_theme && <span>{v.training_theme}</span>}
                          {/* Vincent #401 (Eric 2026-07-01 SPEC STRICK) — Afficher
                              la durée + % dès qu'il y a eu visionnement partiel
                              (pas seulement quand le seuil 75 % est atteint). */}
                          {watchSec > 0 && (
                            <span
                              className={`inline-flex items-center gap-1 font-semibold ${watched ? "text-teal-600" : "text-zinc-600"}`}
                              data-testid={`pathway-learner-video-${v.id}-watch-badge`}
                              title={L(lang,
                                `Vidéo visionnée à ${watchPct}% (${fmtDur(watchSec)})`,
                                `Watched ${watchPct}% (${fmtDur(watchSec)})`)}
                            >
                              {watched ? <CheckCircle size={11} weight="fill" /> : <PlayCircle size={11} weight="fill" />}
                              {fmtDur(watchSec)} ({watchPct}%)
                            </span>
                          )}
                          {watched && watchPct >= 100 && (
                            <span className="px-1.5 py-0.5 rounded-full bg-teal-100 text-teal-800 font-bold text-[10px]" data-testid={`pathway-learner-video-${v.id}-completed-badge`}>
                              {L(lang, "Terminé", "Done")}
                            </span>
                          )}
                        </p>
                      </div>
                      <span className="text-[11px] text-[#002FA7] font-semibold opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1">
                        {/* Vincent B622 (Eric 2026-07-30 SPEC STRICK) — Icône
                            distincte texte (FileText) vs vidéo (PlayCircle). */}
                        {v.is_text_doc ? <FileText size={13} /> : <PlayCircle size={13} />} {L(lang, "Voir →", "Watch →")}
                      </span>
                    </Link>
                    <Link
                      to={`/dashboard/quiz?video_id=${v.id}&auto=1`}
                      onClick={onClose}
                      className={`text-[11px] px-2 py-2 rounded-lg border whitespace-nowrap font-semibold inline-flex flex-col items-center gap-0.5 ${
                        quizPassed
                          ? "border-teal-300 bg-teal-50 hover:bg-teal-100 text-teal-700"
                          : "border-violet-200 bg-violet-50 hover:bg-violet-100 text-violet-700"
                      }`}
                      data-testid={`pathway-learner-video-quiz-${v.id}`}
                      title={
                        quizScore !== null && quizScore !== undefined
                          ? L(lang, `Quiz réalisé — score ${quizScore}%`, `Quiz done — score ${quizScore}%`)
                          : L(lang, "Quiz sur cette vidéo (≥60% = validé)", "Quiz on this video (≥60% = passed)")
                      }
                    >
                      <span className="inline-flex items-center gap-1">
                        {/* Vincent B615 (Eric 2026-07-30 SPEC STRICK) — Retire
                            le logo 🎯 devant « Quiz » ET place le ✓ vert à
                            GAUCHE du mot « Quiz » (parité stricte avec Vue
                            apprenant LearnerPathwaysView.js:378-380). */}
                        {quizPassed && <CheckCircle size={11} weight="fill" data-testid={`pathway-learner-video-${v.id}-quiz-check`} />}
                        {L(lang, "Quiz", "Quiz")}
                        {quizScore !== null && quizScore !== undefined ? (
                          <span className="font-bold" data-testid={`pathway-learner-video-${v.id}-quiz-score-inline`}>({quizScore}%)</span>
                        ) : (
                          <span className="font-normal opacity-70">(— %)</span>
                        )}
                      </span>
                      {quizScore !== null && quizScore !== undefined && (
                        <span className="text-[10px] font-bold" data-testid={`pathway-learner-video-${v.id}-quiz-score`}>{quizScore}%</span>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ol>
          )}
          <p className="mt-3 text-[10px] text-zinc-400">
            {L(lang,
              "Une vidéo est complétée après avoir visionné au moins 30 secondes ET réussi un quiz d'au moins 3 questions sur cette vidéo.",
              "A video is completed after watching at least 30 seconds AND taking a quiz of at least 3 questions on it.")}
          </p>
        </div>

        {/* Footer */}
        {sortedVideos.length > 0 && (
          <div className="px-5 py-3 border-t border-zinc-200 bg-zinc-50 flex items-center justify-end">
            {/* Vincent XLSX 2026-07-13 21h Row 451 (Eric 2026-07-14 SPEC STRICK) —
                Bouton coloré selon éligibilité (rouge/bleu/vert). Voir hook
                usePathwayQuizEligibility. */}
            <PathwayQuizButtonWithEligibility
              pathwayId={pathway.id}
              lang={lang}
              onNavigate={onClose}
              bestQuizScore={bestQuizScore}
            />
          </div>
        )}
      </div>
    </div>
  );
}
